import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Text, VStack, Button, useToast, Image } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useGame } from '../../context/GameContext';

const SingleFlippingCard = ({ 
    cardId = 0, 
    symbol = null, 
    onFlip = () => {}, 
    isFlipped = false, 
    isMatched = false, 
    isRevealed = false,
    disabled = false,
    isBonus = false,
    onBonusComplete = () => {},
    bonusMultiplier = 1,
    betAmount = 1,
    cardColor = "green.500" // Default card color
}) => {
    const [isFlippedState, setIsFlippedState] = useState(isFlipped);
    const [showConfetti, setShowConfetti] = useState(false);
    const [rewardAmount, setRewardAmount] = useState(0);
    const [hasBeenOpened, setHasBeenOpened] = useState(isRevealed);
    const [isHovered, setIsHovered] = useState(false);
    const confettiRef = useRef(null);
    
    // Sync with parent's isFlipped prop
    useEffect(() => {
        setIsFlippedState(isFlipped);
    }, [isFlipped]);
    
    // Sync with parent's isRevealed prop
    useEffect(() => {
        if (isRevealed) {
            setHasBeenOpened(true);
        }
    }, [isRevealed]);
    
    // Handle flip animation
    const handleFlip = () => {
        // Don't allow flipping if disabled or already flipped
        if (disabled || isFlippedState || hasBeenOpened) return;
        
        // Play flip sound
        const flipSound = new Audio('/sounds/card-flip.mp3');
        flipSound.volume = 0.5;
        flipSound.play().catch(e => console.log('Error playing sound:', e));
        
        // Flip the card
        setIsFlippedState(true);
        setHasBeenOpened(true);
        
        // Call the onFlip callback
        onFlip(cardId);
        
        // If it's a bonus card, generate a random reward between 10-100
        if (isBonus) {
            const randomReward = Math.floor(Math.random() * 91) + 10; // 10 to 100
            setRewardAmount(randomReward);
            
            // Trigger confetti effect
            setTimeout(() => {
                setShowConfetti(true);
                
                // Play win sound
                const winSound = new Audio('/sounds/win.mp3');
                winSound.volume = 0.7;
                winSound.play().catch(e => console.log('Error playing sound:', e));
                
                // Notify parent component
                onBonusComplete(randomReward);
            }, 1000);
        }
        
        // Call the parent's onFlip callback
        onFlip(cardId, symbol);
    };
    
    // Launch confetti effect
    const launchConfetti = useCallback((element) => {
        try {
            import('canvas-confetti').then(({ default: confetti }) => {
                const rect = element.getBoundingClientRect();
                const x = rect.left + rect.width / 2;
                const y = rect.top + rect.height / 2;
                
                confetti({
                    particleCount: 100,
                    spread: 70,
                    origin: { 
                        x: x / window.innerWidth,
                        y: y / window.innerHeight
                    }
                });
            });
        } catch (error) {
            console.error("Failed to launch confetti:", error);
        }
    }, []);
    
    // Get symbol icon
    const getSymbolIcon = () => {
        if (!symbol) return '❓';
        
        // Map symbols to emojis
        const symbolMap = {
            Cherry: '🍒',
            Blueberry: '🫐',
            Banana: '🍌',
            Watermelon: '🍉',
            Orange: '🍊',
            Grape: '🍇',
            Aubergine: '🍆',
            Mushroom: '🍄',
            Star: '⭐',
            Wild: '🃏',
            Chest: '📦',
            Wheel: '🎡',
            Ticket: '🎟️',
            Bonus: '🎁',
            Coins: '💰',
            Gems: '💎',
            Key: '🔑'
        };
        
        return symbolMap[symbol] || symbol;
    };
    
    // Get symbol color
    const getSymbolColor = () => {
        if (!symbol) return 'white';
        
        // Map symbols to colors
        const colorMap = {
            Cherry: '#FF5252',
            Blueberry: '#3F51B5',
            Banana: '#FFEB3B',
            Watermelon: '#4CAF50',
            Orange: '#FF9800',
            Grape: '#9C27B0',
            Aubergine: '#673AB7',
            Mushroom: '#F44336',
            Star: '#FFC107',
            Wild: '#FFFFFF',
            Chest: '#FFD700',
            Wheel: '#FF9800',
            Ticket: '#4CAF50',
            Bonus: '#E91E63',
            Coins: '#FFD700',
            Gems: '#00BCD4',
            Key: '#FFC107'
        };
        
        return colorMap[symbol] || 'white';
    };
    
    return (
        <Box
            position="relative"
            w="100%"
            h="100%"
            cursor={disabled || isFlippedState || hasBeenOpened ? 'default' : 'pointer'}
            onClick={handleFlip}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{ perspective: '1000px' }}
        >
            {/* Confetti canvas for bonus cards */}
            {isBonus && (
                <canvas
                    ref={confettiRef}
                    style={{
                        position: 'absolute',
                        pointerEvents: 'none',
                        width: '100%',
                        height: '100%',
                        top: 0,
                        left: 0,
                        zIndex: 10
                    }}
                />
            )}
            
            <Box
                position="relative"
                width="100%"
                height="100%"
                style={{
                    transition: "transform 0.6s",
                    transformStyle: "preserve-3d",
                    transform: isFlippedState ? 'rotateY(180deg)' : 'rotateY(0)'
                }}
            >
                {/* Front - Logo Side */}
                <Box
                    position="absolute"
                    width="100%"
                    height="100%"
                    borderRadius="md"
                    bg="linear-gradient(135deg, #1a202c 0%, #2d3748 100%)"
                    border="2px solid"
                    borderColor={cardColor}
                    boxShadow={`0 4px 8px ${cardColor}33`} // 20% opacity of the card color
                    backfaceVisibility="hidden"
                    style={{ transform: isFlippedState ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
                    transition="transform 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    overflow="hidden"
                    _before={{
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: `linear-gradient(135deg, ${cardColor}1A 0%, ${cardColor}0D 100%)`, // 10% and 5% opacity
                        zIndex: 1
                    }}
                >
                    <Image
                        src="/images/palm-heist-logo.png"
                        alt="Palm Heist Logo"
                        objectFit="contain"
                        w="80%"
                        h="80%"
                    />
                </Box>

                {/* Back - Prize Side */}
                <Box
                    position="absolute"
                    width="100%"
                    height="100%"
                    borderRadius="md"
                    bg="linear-gradient(135deg, #1a202c 0%, #2d3748 100%)"
                    border="2px solid"
                    borderColor={cardColor}
                    boxShadow={`0 4px 8px ${cardColor}33`} // 20% opacity of the card color
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    p={6}
                    overflow="hidden"
                    _before={{
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: `linear-gradient(135deg, ${cardColor}1A 0%, ${cardColor}0D 100%)`, // 10% and 5% opacity
                        zIndex: 1
                    }}
                    style={{
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                        transform: "rotateY(180deg)",
                        zIndex: 1
                    }}
                    _after={{
                        content: '""',
                        position: 'absolute',
                        inset: '-4px',
                        borderRadius: 'xl',
                        padding: '4px',
                        background: 'linear-gradient(45deg, #ff0000, #00ff00, #0000ff, #ff0000, #ffff00, #00ffff)',
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                        animation: 'holoBorder 3s linear infinite',
                        opacity: isHovered ? 0.8 : 0.4,
                        boxShadow: isHovered ? '0 0 15px rgba(255,255,255,0.3)' : 'none'
                    }}
                >
                    {/* Symbol or bonus content */}
                    {isBonus ? (
                        <VStack spacing={2}>
                            <Text fontSize="xl" fontWeight="bold" color="white">
                                Daily Prize
                            </Text>
                            <Text fontSize="4xl" fontWeight="bold" color={cardColor}>
                                {rewardAmount}
                            </Text>
                            <Text fontSize="sm" color="gray.300">
                                JINU Tokens
                            </Text>
                        </VStack>
                    ) : (
                        <Text fontSize="6xl" fontWeight="bold" color={cardColor}>
                            {symbol || '?'}
                        </Text>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default SingleFlippingCard;
